<script>
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, minLength } from '@vuelidate/validators';
import { getRegexp } from 'shared/helpers/Validators';
import { mapGetters } from 'vuex';
import {
  ATTRIBUTE_TYPES,
  EVENT_ATTRIBUTE_TYPES,
  ATTRIBUTE_CATEGORIES,
} from './constants';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';

export default {
  components: {
    NextButton,
    TagInput,
    ConfirmationModal,
  },
  props: {
    selectedAttribute: {
      type: Object,
      default: () => {},
    },
    isUpdating: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['onClose'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      displayName: '',
      description: '',
      attributeType: 0,
      originalAttributeType: 0,
      attributeCategory: null,
      customAttributeCategoryId: null,
      selectedCategoryOption: '',
      regexPattern: null,
      regexCue: null,
      regexEnabled: false,
      show: true,
      attributeKey: '',
      values: [],
      tagInputTouched: false,
    };
  },
  validations: {
    displayName: {
      required,
    },
    attributeType: {
      required,
    },
    description: {
      required,
      minLength: minLength(1),
    },
    attributeKey: {
      required,
      isKey(value) {
        return !(value.indexOf(' ') >= 0);
      },
    },
  },
  computed: {
    ...mapGetters({
      customAttributeCategories: 'customAttributeCategories/getRecords',
    }),
    types() {
      const isEvent =
        this.selectedAttribute?.attribute_model === 'event_attribute' ||
        this.selectedAttribute?.attribute_model === 2;
      const items = isEvent
        ? [...ATTRIBUTE_TYPES, ...EVENT_ATTRIBUTE_TYPES]
        : ATTRIBUTE_TYPES;
      return items.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_TYPES.${item.key}`),
      }));
    },
    categories() {
      return ATTRIBUTE_CATEGORIES.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.${item.key}`),
      }));
    },
    combinedCategoryOptions() {
      const options = [];
      this.categories.forEach(cat => {
        options.push({
          value: `parent_${cat.id}`,
          label: cat.option,
        });
        const subCats = (this.customAttributeCategories || []).filter(
          c => c.parent_category === cat.key.toLowerCase() && c.active
        );
        subCats.forEach(sub => {
          options.push({
            value: `sub_${sub.id}`,
            label: `${cat.option} / ${sub.name}`,
          });
        });
      });
      return options;
    },
    isEventAttribute() {
      return this.selectedAttribute.attribute_model === 'event_attribute';
    },
    setAttributeListValue() {
      return this.selectedAttribute.attribute_values || [];
    },
    updatedAttributeListValues() {
      return this.values;
    },
    isButtonDisabled() {
      return this.v$.description.$invalid || this.isTagInputEmpty;
    },
    isTagInputEmpty() {
      return this.isAttributeTypeList && this.values.length === 0;
    },
    isTagInputInvalid() {
      return this.tagInputTouched && this.isTagInputEmpty;
    },

    pageTitle() {
      return `${this.$t('ATTRIBUTES_MGMT.EDIT.TITLE')} - ${
        this.selectedAttribute.attribute_display_name
      }`;
    },
    selectedAttributeType() {
      return this.types.find(
        item =>
          item.key.toLowerCase() ===
          this.selectedAttribute.attribute_display_type
      )?.id;
    },
    selectedAttributeCategoryId() {
      return (
        this.categories.find(
          item =>
            item.key.toLowerCase() === this.selectedAttribute.attribute_category
        )?.id ?? null
      );
    },
    keyErrorMessage() {
      if (!this.v$.attributeKey.isKey) {
        return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.IN_VALID');
      }
      return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.ERROR');
    },
    isAttributeTypeList() {
      return this.attributeType === 6;
    },
    isAttributeTypeText() {
      return this.attributeType === 0;
    },
    isRegexEnabled() {
      return this.regexEnabled;
    },
    hasTypeChanged() {
      return this.attributeType !== this.originalAttributeType;
    },
  },
  watch: {
    'selectedAttribute.id'(newId, oldId) {
      if (newId && newId !== oldId) {
        this.resetForm();
      }
    },
    selectedCategoryOption(val) {
      if (val && val.startsWith('sub_')) {
        const subId = Number(val.replace('sub_', ''));
        const subCat = (this.customAttributeCategories || []).find(
          c => c.id === subId
        );
        if (subCat) {
          const parent = this.categories.find(
            c => c.key.toLowerCase() === subCat.parent_category
          );
          this.attributeCategory = parent ? parent.id : 5;
          this.customAttributeCategoryId = subId;
        }
      } else if (val && val.startsWith('parent_')) {
        this.attributeCategory = Number(val.replace('parent_', ''));
        this.customAttributeCategoryId = null;
      }
    },
  },
  mounted() {
    this.$store.dispatch('customAttributeCategories/get');
    this.setFormValues();
  },
  methods: {
    onClose() {
      this.$emit('onClose');
    },
    setFormValues() {
      const regexPattern = this.selectedAttribute.regex_pattern
        ? getRegexp(this.selectedAttribute.regex_pattern).source
        : null;
      this.displayName = this.selectedAttribute.attribute_display_name;
      this.description = this.selectedAttribute.attribute_description;
      this.attributeType = this.selectedAttributeType;
      this.originalAttributeType = this.selectedAttributeType;
      this.attributeKey = this.selectedAttribute.attribute_key;
      this.regexPattern = regexPattern;
      this.regexCue = this.selectedAttribute.regex_cue;
      this.regexEnabled = regexPattern != null;
      this.values = this.setAttributeListValue;
      this.attributeCategory = this.selectedAttributeCategoryId;
      this.customAttributeCategoryId =
        this.selectedAttribute.custom_attribute_category_id || null;
      this.tagInputTouched = false;
      if (this.customAttributeCategoryId) {
        this.selectedCategoryOption = `sub_${this.customAttributeCategoryId}`;
      } else if (this.attributeCategory != null) {
        this.selectedCategoryOption = `parent_${this.attributeCategory}`;
      } else {
        this.selectedCategoryOption = '';
      }
    },
    resetForm() {
      this.setFormValues();
      this.v$?.$reset?.();
    },
    onCancel() {
      this.resetForm();
      this.onClose();
    },
    async editAttributes() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      if (this.hasTypeChanged) {
        const confirmed = await this.$refs.confirmTypeChange.showConfirmation();
        if (!confirmed) return;
      }
      if (!this.regexEnabled) {
        this.regexPattern = null;
        this.regexCue = null;
      }
      try {
        const payload = {
          id: this.selectedAttribute.id,
          attribute_description: this.description,
          attribute_display_name: this.displayName,
          attribute_category: this.isEventAttribute
            ? this.attributeCategory
            : null,
          custom_attribute_category_id: this.isEventAttribute
            ? this.customAttributeCategoryId
            : null,
          attribute_values: this.updatedAttributeListValues,
          regex_pattern: this.regexPattern
            ? new RegExp(this.regexPattern).toString()
            : null,
          regex_cue: this.regexCue,
        };
        if (this.hasTypeChanged) {
          payload.attribute_display_type = this.types
            .find(t => t.id === this.attributeType)
            ?.key?.toLowerCase();
        }
        await this.$store.dispatch('attributes/update', payload);
        this.alertMessage = this.$t('ATTRIBUTES_MGMT.EDIT.API.SUCCESS_MESSAGE');
        this.resetForm();
        this.onClose();
      } catch (error) {
        const errorMessage = error?.message;
        this.alertMessage =
          errorMessage || this.$t('ATTRIBUTES_MGMT.EDIT.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
    toggleRegexEnabled() {
      this.regexEnabled = !this.regexEnabled;
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="flex flex-col w-full" @submit.prevent="editAttributes">
      <div class="w-full">
        <woot-input
          v-model="displayName"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.LABEL')"
          type="text"
          :class="{ error: v$.displayName.$error }"
          :error="
            v$.displayName.$error
              ? $t('ATTRIBUTES_MGMT.ADD.FORM.NAME.ERROR')
              : ''
          "
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
          @blur="v$.displayName.$touch"
        />
        <woot-input
          v-model="attributeKey"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.LABEL')"
          type="text"
          :class="{ error: v$.attributeKey.$error }"
          :error="v$.attributeKey.$error ? keyErrorMessage : ''"
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.PLACEHOLDER')"
          readonly
          @blur="v$.attributeKey.$touch"
        />
        <label v-if="isEventAttribute">
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.CATEGORY.LABEL') }}
          <select v-model="selectedCategoryOption">
            <option value="" disabled>
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.CATEGORY.PLACEHOLDER') }}
            </option>
            <option
              v-for="opt in combinedCategoryOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </label>
        <label :class="{ error: v$.description.$error }">
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.LABEL') }}
          <textarea
            v-model="description"
            rows="5"
            type="text"
            :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.DESC.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
          <span v-if="v$.description.$error" class="message">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.ERROR') }}
          </span>
        </label>
        <label :class="{ error: v$.attributeType.$error }">
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LABEL') }}
          <select v-model="attributeType">
            <option v-for="type in types" :key="type.id" :value="type.id">
              {{ type.option }}
            </option>
          </select>
          <span
            v-if="hasTypeChanged"
            class="text-n-ruby-9 mt-1 inline-block mb-4"
          >
            {{ $t('ATTRIBUTES_MGMT.EDIT.TYPE_CHANGE.WARNING_INLINE') }}
          </span>
          <span v-if="v$.attributeType.$error" class="message">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.ERROR') }}
          </span>
        </label>
        <div v-if="isAttributeTypeList" class="mb-4">
          <label class="mb-1 block">
            {{ $t('ATTRIBUTES_MGMT.EDIT.TYPE.LIST.LABEL') }}
          </label>
          <div
            class="rounded-xl border px-3 py-2"
            :class="isTagInputInvalid ? 'border-n-ruby-9' : 'border-n-weak'"
          >
            <TagInput
              v-model="values"
              :placeholder="
                $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.PLACEHOLDER')
              "
              allow-create
              @blur="tagInputTouched = true"
            />
          </div>
          <label
            v-show="isTagInputInvalid"
            class="text-n-ruby-9 dark:text-n-ruby-9 text-sm font-normal mt-1"
          >
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.ERROR') }}
          </label>
        </div>
        <div v-if="isAttributeTypeText">
          <input
            v-model="regexEnabled"
            type="checkbox"
            @input="toggleRegexEnabled"
          />
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.ENABLE_REGEX.LABEL') }}
        </div>
        <woot-input
          v-if="isAttributeTypeText && isRegexEnabled"
          v-model="regexPattern"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.LABEL')"
          type="text"
          :placeholder="
            $t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.PLACEHOLDER')
          "
        />
        <woot-input
          v-if="isAttributeTypeText && isRegexEnabled"
          v-model="regexCue"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.LABEL')"
          type="text"
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.PLACEHOLDER')"
        />
      </div>
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('ATTRIBUTES_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="onCancel"
        />
        <NextButton
          type="submit"
          :label="$t('ATTRIBUTES_MGMT.EDIT.UPDATE_BUTTON_TEXT')"
          :is-loading="isUpdating"
          :disabled="isButtonDisabled"
        />
      </div>
    </form>
    <ConfirmationModal
      ref="confirmTypeChange"
      :title="$t('ATTRIBUTES_MGMT.EDIT.TYPE_CHANGE.TITLE')"
      :description="$t('ATTRIBUTES_MGMT.EDIT.TYPE_CHANGE.DESCRIPTION')"
      :confirm-label="$t('ATTRIBUTES_MGMT.EDIT.TYPE_CHANGE.CONFIRM')"
      :cancel-label="$t('ATTRIBUTES_MGMT.EDIT.TYPE_CHANGE.CANCEL')"
    />
  </div>
</template>

<style lang="scss" scoped>
.key-value {
  padding: 0 0.5rem 0.5rem 0;
  font-family: monospace;
}
</style>
